import React, { useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import getDecryptedToken from "../../../../api/getDecryptedToken";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { patienttitles } from "../../../../utils/title";
import { Autocomplete, Chip, TextField } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PaletteIcon from "@mui/icons-material/Palette";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SchoolIcon from "@mui/icons-material/School";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import TranslateIcon from "@mui/icons-material/Translate";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SaveIcon from "@mui/icons-material/Save";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import type { EmployeeFormState } from "./employeeFormShared";
import { colorOptions, languageOptions, toStringList } from "./employeeFormShared";

interface Role {
  _id: string;
  roleName: string;
}

const AddEmployee: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EmployeeFormState>({
    firstName: "",
    title: "",
    lastName: "",
    email: "",
    password: "",
    confirmpassword: "",
    roleId: "",
    phoneNumber: "",
    address: "",
    employeeId: "",
    color: "",
    imageUrl: "",
    IsDentist: false,
    IsBookDentist: false,
    image: null,
    studies: [],
    designation: "",
    staffType: "",
    slmcNumber: "",
    specialities: [],
    languages: [],
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [employeeExist, setEmployeeExist] = useState(false);
  const [emailExistSameClinic, setEmailExistSameClinic] = useState(false);
  const [emailExistDifferentClinic, setEmailExistDifferentClinic] = useState(false);
  const [passwordNotMatch, setPasswordNotMatch] = useState(false);
  const [pwdLength, setPwdLength] = useState(false);
  const [, setEidLength] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [studiesInput, setStudiesInput] = useState("");
  const [specialitiesInput, setSpecialitiesInput] = useState("");

  const currentClinicId = getDecryptedToken("cid");

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesResponse = await baseUrl.get("/role/findRole", {
          headers: { status: true },
        });
        setRoles(rolesResponse.data.data);

        setFormData({
          firstName: "",
          title: "",
          lastName: "",
          email: "",
          password: "",
          confirmpassword: "",
          roleId: "",
          phoneNumber: "",
          address: "",
          employeeId: "",
          color: "",
          imageUrl: "",
          IsDentist: false,
          IsBookDentist: false,
          image: null,
          studies: [],
          designation: "",
          staffType: "",
          slmcNumber: "",
          specialities: [],
          languages: [],
        });
        setImagePreview(null);
        setEmailExistSameClinic(false);
        setEmailExistDifferentClinic(false);
        setEmployeeExist(false);
        setPasswordNotMatch(false);
        setPwdLength(false);
        setEidLength(false);
      } catch (error) {
        console.log(error);
      }
    };
    fetchRoles();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const checked = target.checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (type === "file" && target.files?.[0]) {
      const file = target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          setFormData((prev) => ({ ...prev, image: file }));
          setImagePreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }

    if (name === "password") {
      setPwdLength(value.length < 8);
      setPasswordNotMatch(value !== formData.confirmpassword);
    }

    if (name === "confirmpassword") {
      setPasswordNotMatch(formData.password !== value);
    }

    if (name === "employeeId") {
      // Only validate length if employee ID is provided (not empty)
      if (value.trim() !== "") {
        setEidLength(value.length < 8);
        baseUrl
          .get("/employee/findEmployeeId", { headers: { employeeid: value } })
          .then((res) => setEmployeeExist(res.data.data.length > 0))
          .catch(console.log);
      } else {
        // Reset validation if field is empty (will be auto-generated)
        setEidLength(false);
        setEmployeeExist(false);
      }
    }

    if (name === "email") {
      const emailValue = value.trim();
      if (emailValue) {
        const headers: Record<string, string> = { email: emailValue };
        baseUrl
          .get("/employee/findEmployeeByEmail", { headers })
          .then((res) => {
            const data = res.data.data || [];
            const currentClinicIdStr = currentClinicId;
            
            // Check if email exists in same clinic or different clinic
            const sameClinicEmployees = data.filter(
              (item: any) => item.clinicId === currentClinicIdStr
            );
            const differentClinicEmployees = data.filter(
              (item: any) => item.clinicId !== currentClinicIdStr
            );
            
            setEmailExistSameClinic(sameClinicEmployees.length > 0);
            setEmailExistDifferentClinic(differentClinicEmployees.length > 0);
          })
          .catch(() => {
            setEmailExistSameClinic(false);
            setEmailExistDifferentClinic(false);
          });
      } else {
        setEmailExistSameClinic(false);
        setEmailExistDifferentClinic(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      // Prevent submission if email exists in same clinic
      if (emailExistSameClinic) {
        alert("Employee with this email already exists in this clinic. Please use a different email.");
        return;
      }

      // Cross-clinic creation scenario: send minimal payload
      if (emailExistDifferentClinic) {
        if (!formData.roleId) {
          alert("Please select a role for the employee.");
          return;
        }
        
        const minimalPayload = {
          email: formData.email,
          roleId: formData.roleId,
        };
        
        await baseUrl.post("/employee/register", minimalPayload);
        navigate(`/client/staff/employee/all-employees`);
        return;
      }

      // Normal creation flow: send full payload
      let payload: any = { ...formData };
      
      // Always remove employeeId - backend will auto-generate it
      delete payload.employeeId;

      if (formData.IsBookDentist) {
        if (formData.image instanceof File) {
          const toBase64 = (file: File) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
            });
          payload.image = await toBase64(formData.image);
        } else if (!formData.imageUrl) {
          alert("Please upload a profile image.");
          return;
        }
        payload.languages = toStringList(formData.languages);
      } else {
        delete payload.image;
        delete payload.languages;
      }
      // Always persist qualifications + specialities (+ certificate fields)
      payload.studies = toStringList([
        ...(formData.studies ?? []),
        ...(studiesInput.trim() ? [studiesInput.trim()] : []),
      ]);
      payload.specialities = toStringList([
        ...(formData.specialities ?? []),
        ...(specialitiesInput.trim() ? [specialitiesInput.trim()] : []),
      ]);
      payload.designation = formData.designation ?? "";
      payload.staffType = formData.staffType ?? "";
      payload.slmcNumber = formData.slmcNumber ?? "";

      if (!employeeExist && !emailExistSameClinic && !passwordNotMatch && !pwdLength) {
        await baseUrl.post("/employee/register", payload);
      }

      navigate(`/client/staff/employee/all-employees`);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePhoneNumberChange = (value: string | undefined) => {
    setFormData((prev) => ({ ...prev, phoneNumber: value || "" }));
  };

  const handleLanguageToggle = (lang: string) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  return (
    <div className="min-w-0 w-full overflow-x-hidden from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 p-3 bg-white dark:bg-slate-900">
      <div className="mx-auto max-w-full">
        {/* Header */}
        <div className="mb-3 md:mb-4">
         
          <div className="flex items-center gap-2.5 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 dark:bg-primary/20 rounded-lg md:rounded-xl flex items-center justify-center">
              <ManageAccountsIcon className="text-primary" sx={{ fontSize: { xs: 18, md: 20 } }} />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-slate-800 dark:text-slate-100">
                Add New Employee
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-0.5">
                Fill in the details to create a new employee
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} autoComplete="off" className="space-y-3">
          {/* Profile Image Section - Hidden when email exists in different clinic */}
          {!(emailExistDifferentClinic && !emailExistSameClinic) && (
            <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <CameraAltIcon className="text-primary" sx={{ fontSize: 16 }} />
              Profile Photo
            </h2>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative group">
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 border-2 border-white dark:border-slate-600 shadow-lg">
                  {imagePreview || formData.imageUrl ? (
                    <img
                      src={imagePreview || formData.imageUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <PersonIcon className="text-slate-300 dark:text-slate-500" sx={{ fontSize: { xs: 40, md: 48 } }} />
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary hover:bg-primaryDark rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-all hover:scale-110">
                  <CameraAltIcon className="text-white" sx={{ fontSize: 16 }} />
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="text-center sm:text-left">
                <p className="text-slate-600 dark:text-slate-300 font-medium text-xs">Upload a profile photo</p>
                <p className="text-slate-400 dark:text-slate-500 text-[10px] mt-0.5">JPG, PNG or GIF. Max size 2MB</p>
              </div>
            </div>
          </div>
          )}

          {/* Basic Information */}
          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700 min-w-0 overflow-hidden">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <PersonIcon className="text-primary" sx={{ fontSize: 16 }} />
              Basic Information
            </h2>
            {emailExistDifferentClinic && !emailExistSameClinic && (
              <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg min-w-0">
                <p className="text-blue-700 dark:text-blue-300 text-xs break-words">
                  This email exists in another clinic. Only Role and Email are required. Other information will be copied from the existing employee record.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
              {/* Role */}
              <div className="space-y-1.5 min-w-0">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Role *</label>
                <div className="relative">
                  <select
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer text-xs text-slate-800 dark:text-slate-200"
                  >
                    <option value="">Select Role</option>
                    {roles.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.roleName}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <KeyboardArrowDownIcon className="text-slate-400" sx={{ fontSize: 16 }} />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5 min-w-0">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Email *</label>
                <div className="relative">
                  <EmailIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    required
                    placeholder="Enter email address"
                    className={`w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 ${
                      emailExistSameClinic ? "border-red-300 dark:border-red-500 focus:border-red-400 focus:ring-red-200" : "border-gray-600 dark:border-slate-600"
                    }`}
                  />
                </div>
                {emailExistSameClinic && (
                  <p className="text-red-500 dark:text-red-400 text-xs flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                    Employee with this email already exists in this clinic.
                  </p>
                )}
                {emailExistDifferentClinic && !emailExistSameClinic && (
                  <p className="text-blue-500 dark:text-blue-400 text-xs flex items-center gap-1">
                    <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                    This email exists in another clinic. A new employee record will be created with minimal information.
                  </p>
                )}
              </div>


              {/* Title - Hidden when email exists in different clinic */}
              {!(emailExistDifferentClinic && !emailExistSameClinic) && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Title *</label>
                  <div className="relative">
                    <select
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required={!(emailExistDifferentClinic && !emailExistSameClinic)}
                      className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer text-xs text-slate-800 dark:text-slate-200"
                    >
                      <option value="">Select Title</option>
                      {patienttitles.map((item: any, i: number) => (
                        <option key={i} value={item.value}>
                          {item.title}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <KeyboardArrowDownIcon className="text-slate-400" sx={{ fontSize: 16 }} />
                    </div>
                  </div>
                </div>
              )}

              {/* First Name - Hidden when email exists in different clinic */}
              {!(emailExistDifferentClinic && !emailExistSameClinic) && (
                <div className="space-y-1.5 min-w-0">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">First Name *</label>
                  <div className="relative">
                    <PersonIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      type="text"
                      required={!(emailExistDifferentClinic && !emailExistSameClinic)}
                      placeholder="Enter first name"
                      className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                </div>
              )}

              {/* Last Name - Hidden when email exists in different clinic */}
              {!(emailExistDifferentClinic && !emailExistSameClinic) && (
                <div className="space-y-1.5 min-w-0">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Last Name *</label>
                  <div className="relative">
                    <PersonIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      type="text"
                      required={!(emailExistDifferentClinic && !emailExistSameClinic)}
                      placeholder="Enter last name"
                      className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                </div>
              )}

              {/* Phone Number - Hidden when email exists in different clinic */}
              {!(emailExistDifferentClinic && !emailExistSameClinic) && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Phone Number *</label>
                  <div className="relative [&_.PhoneInput]:bg-slate-50 dark:[&_.PhoneInput]:bg-slate-700 [&_.PhoneInput]:border [&_.PhoneInput]:border-gray-600 dark:[&_.PhoneInput]:border-slate-600 [&_.PhoneInput]:rounded-lg [&_.PhoneInput]:px-3 [&_.PhoneInput]:py-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:text-xs [&_.PhoneInputInput]:text-slate-800 dark:[&_.PhoneInputInput]:text-slate-200">
                    <PhoneInput
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="LK"
                      value={formData.phoneNumber}
                      onChange={handlePhoneNumberChange}
                    />
                  </div>
                </div>
              )}
              {/* Address - Hidden when email exists in different clinic */}
              {!(emailExistDifferentClinic && !emailExistSameClinic) && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Address *</label>
                  <div className="relative">
                    <LocationOnIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      type="text"
                      required={!(emailExistDifferentClinic && !emailExistSameClinic)}
                      placeholder="Enter address"
                      className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Password Section - Only for new employees, hidden when email exists in different clinic */}
          {!(emailExistDifferentClinic && !emailExistSameClinic) && (
            <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                <LockIcon className="text-primary" sx={{ fontSize: 16 }} />
                Security
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Password */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Password *</label>
                  <div className="relative">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      type="password"
                      required
                      placeholder="Minimum 8 characters"
                      className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                  {pwdLength && (
                    <p className="text-amber-500 dark:text-amber-400 text-xs flex items-center gap-1">
                      <span className="w-1 h-1 bg-amber-500 rounded-full"></span>
                      Password must be at least 8 characters
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Confirm Password *</label>
                  <div className="relative">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="confirmpassword"
                      value={formData.confirmpassword}
                      onChange={handleChange}
                      type="password"
                      required
                      placeholder="Re-enter password"
                      className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                  {passwordNotMatch && (
                    <p className="text-red-500 dark:text-red-400 text-xs flex items-center gap-1">
                      <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                      Passwords do not match
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Color Selection - Hidden when email exists in different clinic */}
          {!(emailExistDifferentClinic && !emailExistSameClinic) && (
            <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <PaletteIcon className="text-primary" sx={{ fontSize: 16 }} />
              Calendar Color
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">
              Choose a color to identify this employee in the calendar
            </p>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((c, index) => (
                <label
                  key={index}
                  className={`relative cursor-pointer group`}
                >
                  <input
                    type="radio"
                    name="color"
                    value={c.color}
                    onChange={handleChange}
                    checked={formData.color === c.color}
                    className="sr-only"
                  />
                  <div
                    className={`w-10 h-10 rounded-lg border border-slate-300/80 shadow-inner transition-all duration-200 ${
                      formData.color === c.color
                        ? "ring-2 ring-offset-2 ring-primary scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-slate-500 dark:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {c.name}
                  </span>
                </label>
              ))}
            </div>
          </div>
          )}

          {/* Professional Details - Hidden when email exists in different clinic */}
          {!(emailExistDifferentClinic && !emailExistSameClinic) && (
            <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <SchoolIcon className="text-primary" sx={{ fontSize: 16 }} />
              Professional Details
            </h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
              Qualifications, designation, and SLMC appear on the dental certificate print footer.
              Staff type appears as the dentist label on bill print.
            </p>
            <div className="space-y-3">
              {/* Studies / Qualifications — certificate footer */}
              <div className="space-y-1.5 professional-details-autocomplete">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <SchoolIcon className="text-slate-400" sx={{ fontSize: 14 }} />
                  Qualifications
                </label>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.studies ?? []}
                  inputValue={studiesInput}
                  onInputChange={(_, value) => setStudiesInput(value)}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, studies: toStringList(newValue) });
                    setStudiesInput("");
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={`${option}-${index}`}
                        label={option}
                        className="!bg-indigo-50 dark:!bg-indigo-900/40 !text-indigo-700 dark:!text-indigo-300 !border-indigo-200 dark:!border-indigo-700"
                        variant="outlined"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Type and press Enter — e.g., BDS (Colombo), MD Orthodontics"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgb(248 250 252)",
                          borderRadius: "0.75rem",
                          fontSize: "0.75rem",
                          minHeight: "2.5rem",
                          padding: "0.25rem 0.5rem",
                          "& .MuiOutlinedInput-input": {
                            padding: "0.25rem 0.25rem !important",
                            fontSize: "0.75rem",
                            lineHeight: "1.5",
                            height: "auto",
                          },
                          "& .MuiAutocomplete-tag": {
                            margin: "0.125rem",
                            height: "1.5rem",
                            fontSize: "0.625rem",
                          },
                          "& fieldset": { 
                            borderColor: "rgb(75 85 99)",
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgb(75 85 99)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "rgb(99 102 241)",
                            borderWidth: "2px",
                          },
                        },
                      }}
                    />
                  )}
                />
              </div>

              {/* Designation — certificate footer */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Designation
                </label>
                <input
                  type="text"
                  name="designation"
                  value={formData.designation ?? ""}
                  onChange={handleChange}
                  placeholder="e.g., Consultant Orthodontist"
                  className="w-full rounded-xl border border-slate-600 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>

              {/* Staff type — bill print dentist label */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Staff Type
                </label>
                <input
                  type="text"
                  name="staffType"
                  value={formData.staffType ?? ""}
                  onChange={handleChange}
                  placeholder="e.g., Dental Surgeon"
                  className="w-full rounded-xl border border-slate-600 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>

              {/* SLMC — certificate footer */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  SLMC Reg. Number
                </label>
                <input
                  type="text"
                  name="slmcNumber"
                  value={formData.slmcNumber ?? ""}
                  onChange={handleChange}
                  placeholder="e.g., 12345"
                  className="w-full rounded-xl border border-slate-600 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>

              {/* Specialities */}
              <div className="space-y-1.5 professional-details-autocomplete">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <MedicalServicesIcon className="text-slate-400" sx={{ fontSize: 14 }} />
                  Specialities
                </label>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.specialities ?? []}
                  inputValue={specialitiesInput}
                  onInputChange={(_, value) => setSpecialitiesInput(value)}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, specialities: toStringList(newValue) });
                    setSpecialitiesInput("");
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={`${option}-${index}`}
                        label={option}
                        className="!bg-purple-50 dark:!bg-purple-900/40 !text-purple-700 dark:!text-purple-300 !border-purple-200 dark:!border-purple-700"
                        variant="outlined"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Type and press Enter — e.g., Orthodontics, Endodontics"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgb(248 250 252)",
                          borderRadius: "0.75rem",
                          fontSize: "0.75rem",
                          minHeight: "2.5rem",
                          padding: "0.25rem 0.5rem",
                          "& .MuiOutlinedInput-input": {
                            padding: "0.25rem 0.25rem !important",
                            fontSize: "0.75rem",
                            lineHeight: "1.5",
                            height: "auto",
                          },
                          "& .MuiAutocomplete-tag": {
                            margin: "0.125rem",
                            height: "1.5rem",
                            fontSize: "0.625rem",
                          },
                          "& fieldset": { 
                            borderColor: "rgb(75 85 99)",
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgb(75 85 99)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "rgb(168 85 247)",
                            borderWidth: "2px",
                          },
                        },
                      }}
                    />
                  )}
                />
              </div>

              {/* Languages */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <TranslateIcon className="text-slate-400" sx={{ fontSize: 14 }} />
                  Languages
                </label>
                <div className="flex flex-wrap gap-2">
                  {languageOptions.map((lang) => (
                    <label
                      key={lang}
                      className={`px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all text-xs ${
                        formData.languages.includes(lang)
                          ? "bg-primary/10 dark:bg-primary/20 border-primary text-primary"
                          : "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500"
                      }`}
                    >
                      <input
                        type="checkbox"
                        value={lang}
                        checked={formData.languages.includes(lang)}
                        onChange={() => handleLanguageToggle(lang)}
                        className="sr-only"
                      />
                      {lang}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Role Toggles - Hidden when email exists in different clinic */}
          {!(emailExistDifferentClinic && !emailExistSameClinic) && (
            <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <ManageAccountsIcon className="text-primary" sx={{ fontSize: 16 }} />
              Employee Type
            </h2>
            <div className="flex flex-wrap gap-3">
              {/* Is Dentist Toggle */}
              <label className="flex items-center gap-2.5 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                <div className="relative">
                  <input
                    type="checkbox"
                    name="IsDentist"
                    checked={formData.IsDentist}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 rounded-full peer-checked:bg-primary transition-colors"></div>
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white dark:bg-slate-200 rounded-full shadow-sm transition-transform peer-checked:translate-x-4"></div>
                </div>
                <span className="font-medium text-xs text-slate-700 dark:text-slate-200">Dentist</span>
              </label>

              {/* Is Book Dentist Toggle */}
              <label className="flex items-center gap-2.5 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                <div className="relative">
                  <input
                    type="checkbox"
                    name="IsBookDentist"
                    checked={formData.IsBookDentist}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 rounded-full peer-checked:bg-primary transition-colors"></div>
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white dark:bg-slate-200 rounded-full shadow-sm transition-transform peer-checked:translate-x-4"></div>
                </div>
                <span className="font-medium text-xs text-slate-700 dark:text-slate-200">Online Booking Dentist</span>
              </label>
              {/* has appointment app */}
            </div>
          </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 rounded-lg border-2 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all flex items-center gap-2 text-xs"
            >
              <SaveIcon sx={{ fontSize: 16 }} />
              Create Employee
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployee;
